<script setup>
import { onMounted, onUnmounted, computed, ref, inject } from 'vue'
import { gsap } from 'gsap'
import './starfield.scss'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const isMobile = inject('isMobileCheck')

let canvasWidth = 0
let canvasHeight = 0
let canvas = null
const effectElem = ref()
const starFieldElem = ref()

let ctx = null
let imageData = null
let pix = null
let center = { x: 0, y: 0 }

const MATHPI2 = Math.PI * 2
let fov = 600
const starHolderCount = 3000
let starHolder = []

const STAR_DISTANCE = 8000
const STAR_SPEED_MAX = 30
const IDLE_SPEED = 0.6
const WARP_THRESHOLD = 3.5

// Состояние движения
let lastDirection = 1 // 1: к нам (вниз), -1: от нас (вверх)
let currentSpeed = IDLE_SPEED
let scrollVelocity = 0
let isScrolling = false
let scrollTimeout = null

let animFrameId = null
let isComponentActive = true
let isFieldVisible = false
let scrollTriggerStars = null

onMounted(() => {
	starfieldInit()
	window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
	isComponentActive = false
	if (animFrameId) {
		cancelAnimationFrame(animFrameId)
	}
	if (scrollTimeout) {
		clearTimeout(scrollTimeout)
	}
	window.removeEventListener('resize', handleResize)
	if (scrollTriggerStars) {
		scrollTriggerStars.kill()
	}
})

function handleResize() {
	if (!canvas || !effectElem.value) return
	canvasWidth = window.innerWidth
	canvasHeight = window.innerHeight
	canvas.setAttribute('width', canvasWidth)
	canvas.setAttribute('height', canvasHeight)
	imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight)
	pix = imageData.data
	center = { x: canvasWidth / 2, y: canvasHeight / 2 }
}

function starfieldInit() {
	canvasWidth = window.innerWidth
	canvasHeight = window.innerHeight
	canvas = document.createElement('canvas')
	canvas.setAttribute('width', canvasWidth)
	canvas.setAttribute('height', canvasHeight)
	canvas.oncontextmenu = (e) => e.preventDefault()

	effectElem.value.appendChild(canvas)
	ctx = canvas.getContext('2d', { alpha: true })
	imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight)
	pix = imageData.data
	center = { x: canvasWidth / 2, y: canvasHeight / 2 }

	if (isMobile.value) {
		fov = 500
	}

	addParticles()
	runScrollTrigger()

	// Запуск постоянного цикла анимации
	isFieldVisible = true
	animloop()
}

function clearImageDataFast() {
	// Встроенный метод fill(0) работает на порядки быстрее ручного for-цикла по 8+ млн элементов
	pix.fill(0)
}

function setPixelAdditive(x, y, r, g, b, a) {
	const i = (x + y * canvasWidth) * 4
	pix[i] = pix[i] + r
	pix[i + 1] = pix[i + 1] + g
	pix[i + 2] = pix[i + 2] + b
	pix[i + 3] = a
}

function drawLine(x1, y1, x2, y2, r, g, b, a) {
	const dx = Math.abs(x2 - x1)
	const dy = Math.abs(y2 - y1)
	const sx = x1 < x2 ? 1 : -1
	const sy = y1 < y2 ? 1 : -1
	let err = dx - dy
	let lx = x1
	let ly = y1

	while (true) {
		if (lx >= 0 && lx < canvasWidth && ly >= 0 && ly < canvasHeight) {
			const i = (lx + ly * canvasWidth) * 4
			pix[i] = r
			pix[i + 1] = g
			pix[i + 2] = b
			pix[i + 3] = a
		}
		if (lx === x2 && ly === y2) break
		const e2 = 2 * err
		if (e2 > -dx) {
			err -= dy
			lx += sx
		}
		if (e2 < dy) {
			err += dx
			ly += sy
		}
	}
}

function addParticle(x, y, z) {
	return {
		x,
		y,
		z,
		x2d: 0,
		y2d: 0,
		w: 1,
		color: { r: 255, g: 255, b: 255, a: 255 },
		oColor: { r: 255, g: 255, b: 255, a: 255 },
		distance: 0,
		distanceTotal: Math.round(STAR_DISTANCE + fov - 1)
	}
}

function addParticles() {
	starHolder = []
	for (let i = 0; i < starHolderCount; i++) {
		const x = Math.random() * 10000 - 5000
		const y = Math.random() * 10000 - 5000
		const z = Math.round(Math.random() * STAR_DISTANCE)

		const colorValue = Math.floor(Math.random() * 155) + 100

		const particle = addParticle(x, y, z)
		particle.color = { r: colorValue, g: colorValue, b: colorValue, a: 255 }
		particle.oColor = { r: colorValue, g: colorValue, b: colorValue, a: 255 }
		particle.distance = STAR_DISTANCE - z

		starHolder.push(particle)
	}
}

function runScrollTrigger() {
	scrollTriggerStars = ScrollTrigger.create({
		trigger: starFieldElem.value,
		start: 'top bottom',
		end: () => 'bottom top',
		pin: effectElem.value,
		scrub: true,
		onToggle: (self) => {
			isFieldVisible = self.isActive
		},
		onUpdate: (self) => {
			// Запоминаем последнее направление скролла
			if (self.direction !== 0) {
				lastDirection = self.direction
			}

			// Скорость от скролла
			scrollVelocity = self.getVelocity() / 60
			isScrolling = true

			// Сброс скролла при остановке
			if (scrollTimeout) {
				clearTimeout(scrollTimeout)
			}
			scrollTimeout = setTimeout(() => {
				isScrolling = false
				scrollVelocity = 0
			}, 90)
		}
	})
}

function animloop() {
	if (!isComponentActive) return

	if (isFieldVisible) {
		renderFrame()
	}

	animFrameId = requestAnimationFrame(animloop)
}

function renderFrame() {
	clearImageDataFast()

	// Целевая скорость: если скроллим — скорость от скролла (не ниже фонового дрейфа), если остановились — плавный дрейф
	let targetSpeed
	if (isScrolling) {
		if (lastDirection >= 0) {
			targetSpeed = Math.max(scrollVelocity, IDLE_SPEED)
		} else {
			targetSpeed = Math.min(scrollVelocity, -IDLE_SPEED)
		}
	} else {
		targetSpeed = lastDirection * IDLE_SPEED
	}

	// Ограничение максимальной скорости в обе стороны
	if (targetSpeed > STAR_SPEED_MAX) targetSpeed = STAR_SPEED_MAX
	if (targetSpeed < -STAR_SPEED_MAX) targetSpeed = -STAR_SPEED_MAX

	// Плавная интерполяция (lerp) скорости — мягкое торможение к фоновому дрейфу
	currentSpeed += (targetSpeed - currentSpeed) * 0.09

	// Расчет величины варп-хвостов
	let warpSpeedValue = 0
	const isWarping = Math.abs(currentSpeed) > WARP_THRESHOLD
	if (isWarping) {
		const divisor = isMobile.value ? STAR_SPEED_MAX : STAR_SPEED_MAX / 2
		warpSpeedValue = currentSpeed * (Math.abs(currentSpeed) / divisor)
	}

	const minZ = -fov + 1
	const maxZ = STAR_DISTANCE

	for (let i = 0, l = starHolder.length; i < l; i++) {
		const star = starHolder[i]

		star.z -= currentSpeed

		// 1. Звезда пролетела мимо камеры (движение вперед/вниз)
		// Наполняем космос: возрождаем новую звезду на далеком фоне
		if (star.z < minZ) {
			star.z = maxZ - (minZ - star.z)
			if (star.z > maxZ) star.z = maxZ

			// Новые случайные координаты и цвет
			star.x = Math.random() * 10000 - 5000
			star.y = Math.random() * 10000 - 5000
			const col = Math.floor(Math.random() * 155) + 100
			star.oColor.r = col
			star.oColor.g = col
			star.oColor.b = col
		}
		// 2. Звезда улетела в бесконечную даль (движение назад/вверх)
		// Наполняем космос: возрождаем новую звезду с внешних краев экрана
		else if (star.z > maxZ) {
			star.z = minZ + (star.z - maxZ)
			if (star.z < minZ) star.z = minZ

			// Появление за пределами экрана на периферии
			const angle = Math.random() * MATHPI2
			const dist = 2500 + Math.random() * 4500
			star.x = Math.cos(angle) * dist
			star.y = Math.sin(angle) * dist

			const col = Math.floor(Math.random() * 155) + 100
			star.oColor.r = col
			star.oColor.g = col
			star.oColor.b = col
		}

		// Расчет яркости: в глубине тусклая/невидимая, вблизи яркая
		const currentDist = STAR_DISTANCE - star.z
		const clampedDist = currentDist < 0 ? 0 : currentDist > star.distanceTotal ? star.distanceTotal : currentDist
		const distancePercent = clampedDist / star.distanceTotal

		star.color.r = (star.oColor.r * distancePercent) | 0
		star.color.g = (star.oColor.g * distancePercent) | 0
		star.color.b = (star.oColor.b * distancePercent) | 0

		// Проекция 3D -> 2D
		const scale = fov / (fov + star.z)
		star.x2d = star.x * scale + center.x
		star.y2d = star.y * scale + center.y

		// Отрисовка светящейся точки
		if (star.x2d >= 0 && star.x2d < canvasWidth && star.y2d >= 0 && star.y2d < canvasHeight) {
			setPixelAdditive(star.x2d | 0, star.y2d | 0, star.color.r, star.color.g, star.color.b, 255)
		}

		// Варп-полосы рисуем только при активном быстром скролле
		if (isWarping) {
			const nz = star.z + warpSpeedValue
			if (nz >= minZ) {
				const nScale = fov / (fov + nz)
				const x2d = star.x * nScale + center.x
				const y2d = star.y * nScale + center.y

				if (x2d >= 0 && x2d < canvasWidth && y2d >= 0 && y2d < canvasHeight) {
					drawLine(star.x2d | 0, star.y2d | 0, x2d | 0, y2d | 0, star.color.r, star.color.g, star.color.b, 255)
				}
			}
		}
	}

	ctx.putImageData(imageData, 0, 0)
}
</script>

<template>
	<div ref="starFieldElem" class="starField">
		<div id="effect" ref="effectElem" class="effect"></div>
	</div>
</template>
